import { formatNameFirstLast } from '@strata/helper-functions';
import { Button } from '@strata/react-buttons';
import { FormContainer } from '@strata/react-form-container';
import { PhoneInputFormik, TextInputFormik } from '@strata/react-inputs';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { FieldArray, Formik } from 'formik';
import { useNavigate, useParams } from 'react-router';

import { PasswordEntry } from 'components/PasswordEntry';
import { DeleteUserDialog } from 'pages/userManagement/dialogs/DeleteUserDialog';
import { CREATE_ROUTE, UserType } from 'services/constants';
import { handleError } from 'services/ErrorManagement';
import { post, put, useGet } from 'services/Http';
import { filterPrescriberRoles, handleDuplicateUserToast } from 'services/UserSupport';
import { getPrescriberUserSchema } from 'services/Validators';

export const PrescriberUserDetails = () => {
    const { upn } = useParams();
    const navigate = useNavigate();

    const goHome = () => navigate(`/user-management/${UserType.PRESCRIBER}`);

    const isCreate = upn === CREATE_ROUTE;

    const user = useGet(!isCreate ? `/authorization/prescriber_users/${upn}` : null);
    const roles = useGet('/authorization/roles/');

    const roleOptions = filterPrescriberRoles(roles);

    return (
        <Formik
            initialValues={{
                upn: user?.upn,
                full_name: user?.prescriber_name,
                sms_number: user?.sms_number,
                practice_npi: user?.practice_npi,
                identities: user?.identities ?? [{}],
            }}
            enableReinitialize
            validationSchema={getPrescriberUserSchema(isCreate)}
            onSubmit={async (values) => {
                try {
                    const [userResult] = await Promise.all([
                        isCreate
                            ? post('/authorization/prescriber_users/', {
                                  upn: values.upn,
                                  given_name: values.given_name,
                                  family_name: values.family_name,
                                  name: formatNameFirstLast(values.given_name, values.family_name),
                                  password: values.password,
                                  sms_number: values.sms_number,
                                  practice_npi: values.practice_npi,
                                  identities: values.identities,
                              })
                            : put(`/authorization/prescriber_users/${values.upn}`, {
                                  prescriber_name: values.full_name,
                                  sms_number: values.sms_number,
                                  practice_npi: values.practice_npi,
                                  identities: values.identities,
                              }),
                        ...values.identities.map((identity) =>
                            put(`/reference/prescribers/rta_user/${identity.prescriber_npi}`),
                        ),
                    ]);

                    if (isCreate && userResult?.authzero_user_created === false) {
                        handleDuplicateUserToast(values.upn, UserType.PRESCRIBER);
                    } else {
                        SuccessToast({
                            message: `Prescriber User: ${values.upn} ${
                                isCreate ? 'created' : 'updated'
                            } successfully.`,
                        });
                    }
                    goHome();
                } catch (err) {
                    handleError('Error saving user.', err);
                }
            }}
        >
            {(formik) => (
                <FormContainer
                    formTitle={`${isCreate ? 'Create' : 'Edit'} Prescriber User`}
                    isLoading={!isCreate && !user}
                    onFormSubmit={formik.handleSubmit}
                    isSubmitting={formik.isSubmitting || !(formik.dirty && formik.isValid)}
                    onSecondaryButtonClick={goHome}
                    formHeaderRightContent={
                        !isCreate && (
                            <DeleteUserDialog
                                userType={UserType.PRESCRIBER}
                                onDeleteSuccess={goHome}
                            />
                        )
                    }
                    formLeftContent={
                        <>
                            <TextInputFormik
                                label="User Email"
                                id="upn"
                                disabled={!isCreate}
                                required
                            />
                            {isCreate ? (
                                <>
                                    <TextInputFormik label="First Name" id="given_name" required />
                                    <TextInputFormik label="Last Name" id="family_name" required />
                                    <PasswordEntry userType={UserType.PRESCRIBER} />
                                </>
                            ) : (
                                <TextInputFormik label="Prescriber Name" id="full_name" required />
                            )}
                            <PhoneInputFormik label="SMS Number" id="sms_number" />
                            <TextInputFormik label="Practice NPI" id="practice_npi" required />
                            <FieldArray name="identities">
                                {(arrayHelpers) => (
                                    <div>
                                        {formik.values[arrayHelpers.name].map(
                                            (_identity, index) => (
                                                <div
                                                    // eslint-disable-next-line react/no-array-index-key
                                                    key={index}
                                                    style={{ display: 'flex' }}
                                                >
                                                    <div className="padding__right--medium">
                                                        <TextInputFormik
                                                            label="NPI"
                                                            id={`${arrayHelpers.name}.${index}.prescriber_npi`}
                                                            required
                                                            helperText="Identity's NPI"
                                                        />
                                                    </div>
                                                    <SuggestSingleSelectFormik
                                                        id={`${arrayHelpers.name}.${index}.role_name`}
                                                        label="Role"
                                                        required
                                                        isLoading={!roles}
                                                        values={roleOptions}
                                                        helperText={
                                                            roleOptions?.find(
                                                                (role) =>
                                                                    role.role_name ===
                                                                    formik.values[
                                                                        arrayHelpers.name
                                                                    ][index].role_name,
                                                            )?.description
                                                        }
                                                        onChange={(newValue) =>
                                                            formik.setFieldValue(
                                                                `${arrayHelpers.name}.${index}.role_name`,
                                                                newValue.role_name,
                                                            )
                                                        }
                                                        selectedItem={roleOptions?.find(
                                                            (role) =>
                                                                role.role_name ===
                                                                formik.values[arrayHelpers.name][
                                                                    index
                                                                ].role_name,
                                                        )}
                                                    />
                                                    <Button
                                                        className="sds_form-button__center-with-inputs margin__left--medium"
                                                        label="Delete"
                                                        onClick={() => arrayHelpers.remove(index)}
                                                    />
                                                </div>
                                            ),
                                        )}
                                        <Button
                                            label="Add Identity"
                                            onClick={() => arrayHelpers.push({})}
                                        />
                                    </div>
                                )}
                            </FieldArray>
                        </>
                    }
                />
            )}
        </Formik>
    );
};
